import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@plotlounge/shared/api";
import { getApiClient } from "../api-client";
import type {
  StaffRoomDetailSegment,
  StaffRoomDetailMember,
  StaffMargauxPost,
  StaffReflection,
  RoomStatus,
} from "@/lib/staff/types";

export interface FacilitatedRoomListItem {
  id: string;
  name: string;
  status: RoomStatus;
  startDate: string;
  endDate: string;
  memberCount: number;
  currentSegment: number;
  totalSegments: number;
  daysUntilStart: number | null;
  hookLine: string | null;
  book: {
    id: string;
    title: string;
    author: string;
    coverUrl: string | null;
  };
}

export interface FacilitatedRoomDetail {
  id: string;
  name: string;
  status: RoomStatus;
  startDate: string;
  endDate: string;
  maxMembers: number | null;
  memberCount: number;
  currentSegment: number;
  totalSegments: number;
  hookLine: string | null;
  book: {
    id: string;
    title: string;
    author: string;
    coverUrl: string | null;
  };
  segments: StaffRoomDetailSegment[];
  members: StaffRoomDetailMember[];
  margauxPosts: StaffMargauxPost[];
  reflections: StaffReflection[];
}

export function useMyFacilitatedRoomsQuery() {
  return useQuery<FacilitatedRoomListItem[]>({
    queryKey: ["facilitator", "rooms"],
    queryFn: () =>
      getApiClient().get<FacilitatedRoomListItem[]>("/facilitator/rooms"),
    staleTime: 30 * 1000,
  });
}

export interface FacilitatedRoomQueryResult {
  status: "loading" | "ok" | "not-found" | "error";
  data?: FacilitatedRoomDetail;
  error?: Error;
}

export function useFacilitatedRoomQuery(id: string | undefined) {
  return useQuery<FacilitatedRoomDetail | null, Error>({
    queryKey: ["facilitator", "rooms", "detail", id],
    enabled: Boolean(id),
    retry: false,
    queryFn: async () => {
      try {
        return await getApiClient().get<FacilitatedRoomDetail>(
          `/facilitator/rooms/${id}`,
        );
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          return null;
        }
        throw err;
      }
    },
    staleTime: 30 * 1000,
  });
}

export interface CreateFacilitatorReflectionInput {
  segmentId: string;
  reflectionText: string;
  quotedReflectionId?: string;
}

export interface CreateFacilitatorReflectionResult {
  id: string;
  isFacilitatorBadge: boolean;
}

export function useCreateFacilitatorReflectionMutation(roomId: string) {
  const qc = useQueryClient();
  return useMutation<
    CreateFacilitatorReflectionResult,
    Error,
    CreateFacilitatorReflectionInput
  >({
    mutationFn: (input) =>
      getApiClient().post<CreateFacilitatorReflectionResult>(
        `/facilitator/rooms/${roomId}/reflections`,
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["facilitator", "rooms", "detail", roomId],
      });
    },
  });
}

export type MargauxScheduleJobName =
  | "weekly-opener"
  | "mid-week-prompt"
  | "sunday-recap";

export interface MargauxScheduleItem {
  jobName: MargauxScheduleJobName;
  label: string;
  scheduledFor: string;
}

export interface FacilitatorScheduleResponse {
  items: MargauxScheduleItem[];
}

export function useMyFacilitatedRoomScheduleQuery(id: string | undefined) {
  return useQuery<FacilitatorScheduleResponse, Error>({
    queryKey: ["facilitator", "rooms", "schedule", id],
    enabled: Boolean(id),
    retry: false,
    queryFn: async () => {
      try {
        return await getApiClient().get<FacilitatorScheduleResponse>(
          `/facilitator/rooms/${id}/schedule`,
        );
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          return { items: [] };
        }
        throw err;
      }
    },
    staleTime: 60 * 1000,
  });
}

export interface HideFacilitatorReflectionResult {
  success: boolean;
  alreadyHidden: boolean;
}

export function useHideFacilitatorReflectionMutation(roomId: string) {
  const qc = useQueryClient();
  return useMutation<HideFacilitatorReflectionResult, Error, string>({
    mutationFn: (reflectionId) =>
      getApiClient().post<HideFacilitatorReflectionResult>(
        `/facilitator/rooms/${roomId}/reflections/${reflectionId}/hide`,
        {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["facilitator", "rooms", "detail", roomId],
      });
    },
  });
}
